/**
 * HTTP Request Interceptors
 *
 * Interceptors that run before each request is sent.
 */

import type { InternalAxiosRequestConfig } from "axios";
import { PUBLIC_AUTH_ENDPOINTS } from "@/config/api";
import { tokenStorage } from "../tokenStorage";
import { getRequestContext } from "../requestContext";
import { httpLogger } from "@/lib/logger";

const isPublicAuthEndpoint = (url?: string): boolean => {
    if (!url) {
        return false;
    }

    const normalizedUrl = url.split("?")[0];

    return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => (
        normalizedUrl === endpoint || normalizedUrl.endsWith(endpoint)
    ));
};

/**
 * Logs outgoing requests (debug level)
 */
export const loggingInterceptor = (
    config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
    const method = config.method?.toUpperCase() ?? "GET";
    const { baseURL, path, resolvedUrl } = getRequestContext(config);

    // Direct console.log for debugging (always visible)
    console.log(`[HTTP] → ${method} ${resolvedUrl}`, {
        baseURL,
        path,
    });
    httpLogger.debug(`${method} ${resolvedUrl} ←`, { baseURL, path });

    return config;
};

/**
 * Adds the Authorization header with the stored access token
 */
export const authInterceptor = (
    config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
    if (isPublicAuthEndpoint(config.url)) {
        if (config.headers) {
            delete config.headers.Authorization;
        }

        return config;
    }

    const token = tokenStorage.getAccessToken();

    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
};

/**
 * Adds common headers to all requests
 */
export const commonHeadersInterceptor = (
    config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
    if (config.headers) {
        config.headers["Content-Type"] = config.headers["Content-Type"] ?? "application/json";
        config.headers["Accept"] = "application/json";
    }

    return config;
};
